import { useState, useEffect, useCallback } from 'react';
import { post } from '../utils/api';
import { API_ENDPOINTS } from '../config/api';

/**
 * Custom hook for fetching safety profile data.
 * Mirrors the same filter shape as useSurvivalAnalysis.
 */
export const useSafetyProfile = (filters, activeIndication, activeModule) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset data immediately when indication changes to avoid stale NHL data showing on B-ALL
  useEffect(() => {
    setData(null);
  }, [activeIndication]);

  const fetchData = useCallback(async () => {
    // Only fetch when on personas module
    if (activeModule !== 'personas') return;

    const shouldFetch =
      filters.activeLevel === 'global' ||
      (filters.activeLevel === 'institute' && filters.selectedInstitute) ||
      (filters.activeLevel === 'region' && filters.selectedRegion) ||
      (filters.activeLevel === 'physician' && filters.selectedPhysician) ||
      (filters.activeLevel === 'my_patients' && filters.myPhysicianName);

    if (!shouldFetch) return;

    setLoading(true);
    setError(null);

    try {
      // Use same legacy filter mapping as survivalAnalysisService — ensures
      // both APIs filter identically and persona n counts stay in sync
      let filter_type = 'global';
      let filter_value = 'all';

      if (filters.activeLevel === 'institute' && filters.selectedInstitute) {
        filter_type = 'account';
        filter_value = filters.selectedInstitute;
      } else if (filters.activeLevel === 'region' && filters.selectedRegion) {
        filter_type = 'zone';
        filter_value = filters.selectedRegion;
      } else if (filters.activeLevel === 'physician' && filters.selectedPhysician) {
        filter_type = 'hcp';
        filter_value = filters.selectedPhysician;
      } else if (filters.activeLevel === 'my_patients' && filters.myPhysicianName) {
        filter_type = 'hcp';
        filter_value = filters.myPhysicianName;
      }

      const payload = {
        indication: activeIndication === 'NHL' ? 'nhl' : 'all',
        lines_of_failure: filters.linesOfFailure || 'all',
        api_version: 1.1,
        filter_type,
        filter_value,
        selected_institute: filters.selectedInstitute || '',
        selected_regions: filters.selectedRegion ? [filters.selectedRegion] : [],
        selected_physicians: filters.activeLevel === 'my_patients' && filters.myPhysicianName
          ? [filters.myPhysicianName]
          : (filters.selectedPhysician ? [filters.selectedPhysician] : []),
      };

      if (filters.apheresisCutoff) payload.apheresis_cutoff = filters.apheresisCutoff;

      const result = await post(API_ENDPOINTS.safetyProfile, payload);
      setData(result);
    } catch (err) {
      console.error('❌ Safety profile fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [
    activeIndication,
    activeModule,
    filters.activeLevel,
    filters.selectedInstitute,
    filters.selectedRegion,
    filters.selectedPhysician,
    filters.myPhysicianName,
    filters.linesOfFailure,
    filters.apheresisCutoff,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error };
};
